import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { TIDS } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";

export default function Nav() {
  const { user, logout, setAuthOpen } = useAuth();
  const navigate = useNavigate();
  const routerLoc = useRouterLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.0, delay: 1.0, ease: [0.7, 0, 0.2, 1] }}
      className="fixed top-0 inset-x-0 z-50"
    >
      <div className="px-5 sm:px-6 md:px-10 pt-4 sm:pt-5 transition-all duration-500">
        <div className={`flex items-center justify-between rounded-2xl px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 transition-all duration-500 ${scrolled ? "glass-deep" : ""}`}>
          {/* Left: logo — contained cleanly within the pill */}
          <Link to="/" data-testid={TIDS.navLogo} className="flex items-center gap-3 leading-none shrink-0" aria-label="Thai Gayu home">
            <img
              src="/thaigayu-logo-gold.png"
              alt="Thai Gayu"
              width="156"
              height="48"
              decoding="async"
              fetchPriority="high"
              className="h-10 sm:h-11 md:h-12 w-auto select-none block"
              style={{ mixBlendMode: "lighten", filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.45))" }}
              draggable={false}
            />
            <span className="hidden lg:inline-block w-px h-3" style={{ background: "var(--divider)" }} />
            <span className="hidden lg:inline section-num">ancient science · modern timing</span>
          </Link>

          {/* Right: nav */}
          <nav className="flex items-center gap-4 md:gap-7 text-sm">
            <Link
              to="/"
              data-testid={TIDS.navHome}
              className="hidden md:inline-block text-sm uppercase tracking-[0.18em] transition-colors duration-300 hover:!text-[#F2E2B0]"
              style={{ color: routerLoc.pathname === "/" ? "#F2E2B0" : "#EFEAD8" }}
            >
              Home
            </Link>
            <a
              href="#about"
              data-testid={TIDS.navAbout}
              className="hidden md:inline-block text-sm uppercase tracking-[0.18em] transition-colors duration-300 hover:!text-[#F2E2B0]"
              style={{ color: "#EFEAD8" }}
            >
              About
            </a>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  data-testid={TIDS.navDashboard}
                  className="link-underline text-xs uppercase tracking-[0.18em]"
                  style={{ color: "var(--ink-muted)" }}
                >
                  Dashboard
                </Link>
                <button
                  data-testid={TIDS.navLogout}
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="px-4 py-2 rounded-full border bg-white/45 backdrop-blur hover:border-[var(--sage-700)] hover:text-[var(--sage-700)] transition-colors duration-500 text-[10px] uppercase tracking-[0.22em]"
                  style={{ borderColor: "rgba(26,28,26,0.15)", color: "var(--ink)" }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <button
                data-testid={TIDS.navAuth}
                onClick={() => setAuthOpen(true)}
                className="btn-primary px-4 sm:px-5 py-2 rounded-xl text-xs uppercase tracking-[0.22em] font-medium whitespace-nowrap"
              >
                Sign in
              </button>
            )}
          </nav>
        </div>
      </div>
    </motion.header>
  );
}
